import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Switch,
} from "@mui/material";
import { AreaChart, Area, PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import DynamicFeedOutlinedIcon from '@mui/icons-material/DynamicFeedOutlined';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

const AILearningFeedback = () => {
    const navigate = useNavigate();

    const signalsData = [
        {
            signal: "Add to Cart",
            impact: "Accepted",
            details: "FloraOutlet (3 times)",
            weight: "50%",
            trend: "up",
            action: "Accept"
        },
        {
            signal: "Purchase",
            impact: "Rejected",
            details: "PetalUAE (consider Fraudunt)",
            weight: "60%",
            trend: "up_strong",
            action: "Reject"
        },
        {
            signal: "Wishlist Add",
            impact: "Accepted",
            details: "JewellHome (22 times) →",
            weight: "50%",
            trend: "up",
            action: "Reject"
        },
        {
            signal: "Vendor Rating",
            impact: "Overridden",
            details: "DecoStyle (1.4 star rating) →",
            weight: "40%",
            trend: "up_slight",
            action: "Reject"
        },
        {
            signal: "CraftZone",
            impact: "Overridden",
            details: "CraftZone (1 star rating mismatch)",
            weight: "40%",
            trend: "up_slight",
            action: "Reject"
        }
    ];

    const getImpactStyle = (impact: string) => {
        switch (impact) {
            case 'Accepted':
                return { color: '#2E7D32', background: '#E8F5E9', border: '1px solid #C8E6C9' };
            case 'Rejected':
                return { color: '#C62828', background: '#FFEBEE', border: '1px solid #FFCDD2' };
            case 'Overridden':
                return { color: '#E65100', background: '#FFF3E0', border: '1px solid #FFE0B2' };
            default:
                return { color: '#666', background: '#f5f5f5', border: '1px solid #e0e0e0' };
        }
    };

    const getImpactIcon = (impact: string) => {
        switch (impact) {
            case 'Accepted':
                return <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />;
            case 'Rejected':
                return <CancelOutlinedIcon sx={{ fontSize: 14 }} />;
            case 'Overridden':
                return <ErrorOutlineIcon sx={{ fontSize: 14 }} />;
            default:
                return null;
        }
    };

    return (
        <>
            <div className="main_title">
                <h1>AI Learning & Feedback Signals Management</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> - AI Learning & Feedback Signals Management
                </p>
            </div>

            <div className="stats_boxes gap_m mb_20">
                {/* CARD 1 */}
                <div className="cards">
                    <div className="data_top">
                        <h2>
                            86
                            <p>Learning Trust Score</p>
                        </h2>
                        <figure className="sucess">
                            <VerifiedUserOutlinedIcon fontSize="large" />
                        </figure>
                    </div>
                    <div className="ai_insight positive">
                        <span>🛡️ Healthy</span>
                    </div>
                </div>

                {/* CARD 2 */}
                <div className="cards">
                    <div className="data_top">
                        <h2>
                            70.5%
                            <p>AI-Halted Nuggets</p>
                        </h2>
                        <figure className="warning">
                            <BlockOutlinedIcon fontSize="large" />
                        </figure>
                    </div>
                    <div className="ai_insight positive">
                        <span>▲ 2.7% YoY • 2.7K on last 30 days</span>
                    </div>
                </div>

                {/* CARD 3 */}
                <div className="cards">
                    <div className="data_top">
                        <h2>
                            12.6%
                            <p>Signals Processed</p>
                        </h2>
                        <figure className="info">
                            <DynamicFeedOutlinedIcon fontSize="large" />
                        </figure>
                    </div>
                    <div className="ai_insight">
                        <span style={{ color: '#8B6B4C' }}>✦ 631K total messages</span>
                    </div>
                </div>

                {/* CARD 4 */}
                <div className="cards">
                    <div className="data_top">
                        <h2>
                            112,794
                            <p>High Confidence</p>
                        </h2>
                        <figure className="purple" style={{ backgroundColor: "#9B87EA" }}>
                            <ThumbUpAltOutlinedIcon fontSize="large" />
                        </figure>
                    </div>
                    <div className="ai_insight positive">
                        <span>▲ 63% on last 30 days</span>
                    </div>
                </div>
            </div>
            <div className="table_flex">
                <div className="lt">

                    <div className="cards table_card mt_20">
                        <div className="cards_header">
                            <div className="left_s">
                                <h2>Feedback Signals Overview</h2>
                            </div>
                            <div className="right_s" style={{ display: 'flex', gap: '10px' }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<CheckIcon sx={{ color: '#333' }} />}
                                    sx={{ color: '#333', borderColor: '#e0e0e0', textTransform: 'none', backgroundColor: '#fafafa', '&:hover': { backgroundColor: '#f0f0f0' } }}
                                >
                                    Accept All
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<CancelOutlinedIcon sx={{ color: '#999' }} />}
                                    sx={{ color: '#999', borderColor: '#e0e0e0', textTransform: 'none', backgroundColor: '#fafafa', '&:hover': { backgroundColor: '#f0f0f0' } }}
                                >
                                    Reject All
                                </Button>
                            </div>
                        </div>

                        <TableContainer className="table_container">
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#FCFCFC' }}>
                                        <TableCell sx={{ color: '#666', fontSize: '12px', fontWeight: 600 }}>Signal</TableCell>
                                        <TableCell sx={{ color: '#666', fontSize: '12px', fontWeight: 600 }}>Learning Impact</TableCell>
                                        <TableCell sx={{ color: '#666', fontSize: '12px', fontWeight: 600 }}>Signal Details</TableCell>
                                        <TableCell sx={{ color: '#666', fontSize: '12px', fontWeight: 600 }}>Weight</TableCell>
                                        <TableCell sx={{ color: '#666', fontSize: '12px', fontWeight: 600, textAlign: 'center' }}>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {signalsData.map((row, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell sx={{ color: '#333', fontSize: '13px', fontWeight: 500 }}>
                                                {row.signal}
                                            </TableCell>
                                            <TableCell>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span style={{
                                                        display: 'flex', alignItems: 'center', gap: '4px',
                                                        padding: '4px 12px', borderRadius: '16px', fontSize: '11px', fontWeight: 600,
                                                        ...getImpactStyle(row.impact)
                                                    }}>
                                                        {getImpactIcon(row.impact)}
                                                        {row.impact}
                                                    </span>
                                                    {row.impact === 'Rejected' && <span style={{ fontSize: '10px', color: '#999' }}>False ➔</span>}
                                                    {row.impact === 'Overridden' && <span style={{ fontSize: '12px', color: '#E65100', letterSpacing: '-2px' }}>● ➔</span>}
                                                </div>
                                            </TableCell>
                                            <TableCell sx={{ color: '#666', fontSize: '12px' }}>
                                                {row.details}
                                            </TableCell>
                                            <TableCell>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#666', fontSize: '13px', fontWeight: 500 }}>
                                                    {row.impact === 'Overridden' ? <span style={{ color: '#DDA057' }}>⟳</span> : <span style={{ color: '#4caf50' }}>●</span>}
                                                    {row.weight}
                                                    {row.trend === 'up' && <TrendingUpIcon sx={{ color: '#4caf50', fontSize: 16 }} />}
                                                    {row.trend === 'up_strong' && <TrendingUpIcon sx={{ color: '#2E7D32', fontSize: 18, fontWeight: 'bold' }} />}
                                                    {row.trend === 'up_slight' && <TrendingUpIcon sx={{ color: '#DDA057', fontSize: 16 }} />}
                                                </div>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: row.action === 'Accept' ? '#E8F5E9' : '#fafafa',
                                                        color: row.action === 'Accept' ? '#2E7D32' : '#666',
                                                        border: '1px solid',
                                                        borderColor: row.action === 'Accept' ? '#C8E6C9' : '#e0e0e0',
                                                        textTransform: 'none',
                                                        boxShadow: 'none',
                                                        minWidth: '70px',
                                                        padding: '2px 10px',
                                                        '&:hover': {
                                                            backgroundColor: row.action === 'Accept' ? '#C8E6C9' : '#f0f0f0',
                                                            boxShadow: 'none'
                                                        }
                                                    }}
                                                >
                                                    {row.action}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '15px' }}>
                            <Button
                                size="small"
                                endIcon={<KeyboardArrowRightIcon fontSize="small" />}
                                sx={{ color: '#666', textTransform: 'none', fontSize: '12px', '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' } }}
                            >
                                Export Signals
                            </Button>
                        </div>
                    </div>

                    <div className="table_flex mt_20">
                        <div className="lt">
                            <div className="cards ai_model_retraining" style={{ height: '100%' }}>
                                <div className="header">
                                    <h2>Model Retraining Schedule</h2>
                                    <KeyboardArrowRightIcon sx={{ color: '#999' }} />
                                </div>
                                <div className="sub_header">
                                    <span style={{ fontSize: '11px', color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}><DynamicFeedOutlinedIcon sx={{ fontSize: 14 }} /> Max retraining</span>
                                    <span style={{ fontSize: '11px', color: '#666', borderLeft: '1px solid #ddd', paddingLeft: '15px' }}>Test V.1</span>
                                    <span className="badge">1.0k</span>
                                    <span style={{ fontSize: '11px', color: '#666' }}>Variant: 1,400</span>
                                </div>

                                <div className="table_row" style={{ backgroundColor: '#f9f9f9', borderRadius: '4px 4px 0 0', padding: '8px 10px', borderBottom: 'none' }}>
                                    <div className="col_name" style={{ color: '#666', fontSize: '11px' }}>Next Retraining</div>
                                    <div className="col_pct" style={{ color: '#666', fontSize: '11px', fontWeight: 500 }}><VerifiedUserOutlinedIcon sx={{ fontSize: 12 }} /> 0 Hosts PR</div>
                                    <div className="col_action" style={{ color: '#999', fontSize: '11px' }}>00 Days <KeyboardArrowRightIcon sx={{ fontSize: 14 }} /></div>
                                </div>

                                <div className="table_row">
                                    <div className="col_name">Product Add</div>
                                    <div className="col_pct">20% <TrendingUpIcon sx={{ fontSize: 14, color: '#4caf50' }} /></div>
                                    <div className="col_action">
                                        <span className="btn_status accept_teal">Accept</span>
                                        <Switch size="small" defaultChecked color="success" />
                                    </div>
                                </div>
                                <div className="table_row">
                                    <div className="col_name">Add to Cart</div>
                                    <div className="col_pct">25% <TrendingUpIcon sx={{ fontSize: 14, color: '#DDA057' }} /></div>
                                    <div className="col_action">
                                        <span className="btn_status accept_brown">Accept</span>
                                        <Switch size="small" defaultChecked color="success" />
                                    </div>
                                </div>
                                <div className="table_row">
                                    <div className="col_name">Purchase impact</div>
                                    <div className="col_pct">35% <TrendingUpIcon sx={{ fontSize: 14, color: '#2E7D32' }} /></div>
                                    <div className="col_action">
                                        <span className="btn_status override_red">Override</span>
                                        <Switch size="small" defaultChecked color="success" />
                                    </div>
                                </div>
                                <div className="table_row">
                                    <div className="col_name">Vendor Rating</div>
                                    <div className="col_pct">10% <TrendingUpIcon sx={{ fontSize: 14, color: '#4caf50' }} /></div>
                                    <div className="col_action">
                                        <span className="btn_status reject_gray">Reject</span>
                                        <Switch size="small" color="default" />
                                    </div>
                                </div>
                                <div className="table_row">
                                    <div className="col_name">Journal Share</div>
                                    <div className="col_pct">10% <TrendingUpIcon sx={{ fontSize: 14, color: '#4caf50', transform: 'scaleY(-1)' }} /></div>
                                    <div className="col_action">
                                        <span className="btn_status reject_gray">Reject</span>
                                        <Switch size="small" color="default" />
                                    </div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '15px' }}>
                                    <Button
                                        size="small"
                                        endIcon={<KeyboardArrowRightIcon fontSize="small" />}
                                        startIcon={<DynamicFeedOutlinedIcon fontSize="small" />}
                                        sx={{ color: '#666', textTransform: 'none', fontSize: '12px', '&:hover': { backgroundColor: 'transparent' } }}
                                    >
                                        Access Signals
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className="rt">
                            <div className="cards ai_signal_weights mb_20">
                                <div className="cards_header">
                                    <div className="left_s">
                                        <h2>Signal Weights <span style={{ fontSize: '12px', color: '#666', marginLeft: '5px', fontWeight: 'normal' }}><KeyboardArrowRightIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> 20%</span></h2>
                                    </div>
                                </div>
                                <div className="chart_container">
                                    <div className="chart">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={[{ v: 10 }, { v: 6 }, { v: 5 }, { v: 8 }, { v: 12 }, { v: 16 }, { v: 22 }, { v: 30 }]}>
                                                <defs>
                                                    <linearGradient id="colorWeight" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#DDA057" stopOpacity={0.3} />
                                                        <stop offset="95%" stopColor="#DDA057" stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <Area type="monotone" dataKey="v" stroke="#DDA057" strokeWidth={2} fillOpacity={1} fill="url(#colorWeight)" dot={{ r: 2, fill: '#DDA057' }} />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div className="stats" style={{ paddingLeft: '15px', borderLeft: '1px solid #f0f0f0' }}>
                                        <div style={{ marginBottom: '15px' }}>
                                            <h3 style={{ display: 'flex', alignItems: 'center' }}>4% <TrendingUpIcon sx={{ fontSize: 20, color: '#999', transform: 'rotate(90deg)', marginLeft: '8px' }} /></h3>
                                            <p style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                                <span style={{ width: '15px', height: '4px', backgroundColor: '#F8F1E5', borderRadius: '2px' }}></span>
                                                Free Action
                                            </p>
                                        </div>
                                        <div>
                                            <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#333' }}>Low -36% <span style={{ color: '#4caf50', fontSize: '11px', fontWeight: 'normal' }}>↑ 1.6%</span></h4>
                                            <p style={{ fontSize: '10px', marginTop: '3px' }}><VerifiedUserOutlinedIcon sx={{ fontSize: 10, color: '#4caf50' }} /> Artificial Group near transports</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="cards ai_override_patterns" style={{ marginBottom: 0 }}>
                                <div className="cards_header" style={{ marginBottom: '10px' }}>
                                    <div className="left_s">
                                        <h2>Signal Override Patterns</h2>
                                    </div>
                                    <div className="right_s">
                                        <Button variant="outlined" size="small" sx={{ color: '#666', borderColor: '#e0e0e0', textTransform: 'none', borderRadius: '16px', fontSize: '11px', padding: '2px 10px' }}>Adjust: Weights</Button>
                                    </div>
                                </div>
                                <div className="content">
                                    <div className="list">
                                        <div className="list_item" style={{ fontSize: '10px', color: '#999', borderBottom: 'none', padding: '0 0 4px' }}>
                                            <span>ORIOSE</span>
                                            <span>crit/facrite</span>
                                        </div>
                                        <div className="list_item">
                                            <span className="name"><span style={{ color: '#2A818F' }}>■</span> eShop / wishlist</span>
                                            <span className="pct">4.0%</span>
                                        </div>
                                        <div className="list_item">
                                            <span className="name"><span style={{ color: '#DC5E45' }}>■</span> ePrice Macro docs</span>
                                            <span className="pct">10.0%</span>
                                        </div>
                                        <div className="list_item">
                                            <span className="name"><span style={{ color: '#DDA057' }}>■</span> Auto Display</span>
                                            <span className="pct">8.0%</span>
                                        </div>
                                        <div className="list_item" style={{ borderBottom: 'none' }}>
                                            <span className="name"><span style={{ color: '#999' }}>○</span> Pounds outreach →</span>
                                            <span className="pct">4.%</span>
                                        </div>
                                    </div>
                                    <div className="chart">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={[{ v: 11 }, { v: 30 }, { v: 43 }, { v: 16 }]}
                                                    dataKey="v"
                                                    outerRadius={55}
                                                    innerRadius={0}
                                                    stroke="#fff"
                                                    strokeWidth={2}
                                                    startAngle={90}
                                                    endAngle={-270}
                                                >
                                                    <Cell fill="#2A818F" /> {/* Teal */}
                                                    <Cell fill="#B37A4C" /> {/* Brown */}
                                                    <Cell fill="#DDA057" /> {/* Yellow */}
                                                    <Cell fill="#8A92A6" /> {/* Gray */}
                                                </Pie>
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="rt">
                    <div className="ai_sidebar_card">
                        <div className="header">
                            <h3>Model Retraining Schedule</h3>
                        </div>
                        <div className="info_row primary">
                            <span className="dot yellow"></span>
                            <span className="text">Retrain in <strong>2 days</strong></span>
                            <span className="badge">B3 2000s</span>
                        </div>
                        <div className="info_row secondary">
                            <DynamicFeedOutlinedIcon sx={{ fontSize: 12 }} /> 18.7% every 2 weeks
                        </div>

                        <div className="btn_fullwidth outline">
                            <div className="left">
                                <span className="dot teal"></span> Approve Retraining
                            </div>
                            <KeyboardArrowRightIcon sx={{ fontSize: 16, transform: 'rotate(90deg)', color: '#999' }} />
                        </div>

                        <div className="btn_group_three">
                            <Button className="btn_act"><VerifiedUserOutlinedIcon sx={{ fontSize: 14 }} /> Skip</Button>
                            <Button className="btn_act active">Pause</Button>
                            <Button className="btn_act">Retraining Locked</Button>
                        </div>

                        <div className="btn_group_two bordered_top">
                            <Button className="btn_act outline"><DynamicFeedOutlinedIcon sx={{ fontSize: 14 }} /> Adjust Schedule</Button>
                            <Button className="btn_act outline">Report</Button>
                        </div>
                    </div>

                    <div className="ai_sidebar_card">
                        <div className="header">
                            <h3>Retraining Actions</h3>
                        </div>
                        <div className="action_row">
                            <div className="label"><span className="dot teal"></span> Enable Automated Retraining</div>
                            <Switch size="small" defaultChecked color="success" />
                        </div>
                        <div className="action_row">
                            <div className="label"><DynamicFeedOutlinedIcon sx={{ fontSize: 14, color: '#2A818F' }} /> Automatically adapt & learn</div>
                            <Switch size="small" defaultChecked color="success" />
                        </div>
                        <div className="action_row bordered">
                            <div className="label"><span className="dot gray"></span> Force Retraining</div>
                            <Button className="status_btn alert" style={{ minWidth: '95px' }}>Revision Needed</Button>
                        </div>
                        <div className="action_row">
                            <div className="label"><span className="dot gray"></span> Freeze Learning</div>
                            <Button className="status_btn info" style={{ minWidth: '95px' }}>Override Surge</Button>
                        </div>
                    </div>

                    <div className="ai_sidebar_card" style={{ paddingBottom: '5px' }}>
                        <div className="header">
                            <h3>Model Override Patterns</h3>
                        </div>
                        <div className="pattern_list">
                            <div className="pattern_item">
                                <div className="icon warning"><DynamicFeedOutlinedIcon sx={{ fontSize: 16 }} /></div>
                                <div className="details">
                                    <h4>Adjustment: HA- 2407</h4>
                                    <p>Option: Base Adjustment <span className="float_rt">Aug 16, 2:04 PM</span></p>
                                </div>
                            </div>
                            <div className="pattern_item">
                                <div className="icon group"><VerifiedUserOutlinedIcon sx={{ fontSize: 16 }} /></div>
                                <div className="details">
                                    <h4 style={{ margin: 0 }}>Vendor Block: <span className="sub">Flagged</span> <span className="float_rt sub">Feb 6, 20:32 AM</span></h4>
                                </div>
                            </div>
                            <div className="pattern_item" style={{ marginTop: '10px' }}>
                                <div className="icon warning"><DynamicFeedOutlinedIcon sx={{ fontSize: 16 }} /></div>
                                <div className="details">
                                    <h4>Price Adjustment: <span className="sub">Floor/Height above threshold</span></h4>
                                    <p>Automated: Freestance</p>
                                </div>
                            </div>
                            <div className="pattern_item">
                                <div className="icon warning"><ThumbUpAltOutlinedIcon sx={{ fontSize: 16 }} /></div>
                                <div className="details">
                                    <h4 style={{ margin: 0 }}>Learning Upsell: <span className="sub">Generated</span> <span className="float_rt sub">Sep 25, 01:30 PM</span></h4>
                                </div>
                            </div>
                        </div>
                        <div className="btn_group_two bordered_top">
                            <Button className="btn_act outline">Outbreak</Button>
                            <Button className="btn_act outline"><CheckCircleOutlineIcon sx={{ fontSize: 14 }} /> Generate Insights</Button>
                        </div>
                    </div>

                    <Button className="btn_generate_report" fullWidth>
                        Generate Report <KeyboardArrowRightIcon sx={{ fontSize: 16, marginLeft: '4px' }} />
                    </Button>
                </div>
            </div>
        </>
    );
};

export default AILearningFeedback;
